import { useEffect, useState, useCallback, useRef } from "react";
import type {
  GetOtpBalanceResponse,
  NotificationContact,
} from "@codway-dev/shared";
import { OtpBalanceService } from "../services/otp-balance.service";
import { buildAdminAppUrl } from "../services/admin-deeplink";

interface OtpBalanceState {
  data: GetOtpBalanceResponse | null;
  loading: boolean;
  reloading: boolean;
  error: string | null;
  buying: string | null;
  updatingContacts: boolean;
}

interface OtpBalanceActions {
  buyPack: (package_id: string) => Promise<void>;
  updateNotificationContacts: (
    contacts: NotificationContact[]
  ) => Promise<void>;
  reload: () => void;
}

export function useOtpBalance(): OtpBalanceState & OtpBalanceActions {
  const [state, setState] = useState<OtpBalanceState>({
    data: null,
    loading: true,
    reloading: false,
    error: null,
    buying: null,
    updatingContacts: false,
  });

  const [reloadSignal, setReloadSignal] = useState(0);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const isFirstLoad = !hasLoadedRef.current;
      setState((s) => ({
        ...s,
        loading: isFirstLoad,
        reloading: !isFirstLoad,
        error: null,
      }));
      try {
        const data = await OtpBalanceService.getBalance();
        if (!cancelled) {
          hasLoadedRef.current = true;
          setState((s) => ({ ...s, data, loading: false, reloading: false }));
        }
      } catch (err) {
        if (!cancelled)
          setState((s) => ({
            ...s,
            loading: false,
            reloading: false,
            error: err instanceof Error ? err.message : "Error desconocido",
          }));
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [reloadSignal]);

  // Reload when the tab regains focus — covers the case where the merchant
  // approves a purchase in the Shopify billing tab and then closes it.
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        setReloadSignal((n) => n + 1);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const reload = useCallback(() => setReloadSignal((n) => n + 1), []);

  const buyPack = useCallback(async (package_id: string) => {
    setState((s) => ({ ...s, buying: package_id, error: null }));
    try {
      const returnUrl = buildAdminAppUrl("security-editor/otp");
      const { confirmation_url } = await OtpBalanceService.topup(
        package_id,
        returnUrl
      );
      localStorage.setItem("codway_otp_billing_pending", "1");
      window.open(confirmation_url, "_top");
    } catch (err) {
      setState((s) => ({
        ...s,
        buying: null,
        error: err instanceof Error ? err.message : "Error al iniciar compra",
      }));
      return;
    }
    setState((s) => ({ ...s, buying: null }));
  }, []);

  const updateNotificationContacts = useCallback(
    async (contacts: NotificationContact[]) => {
      setState((s) => ({ ...s, updatingContacts: true, error: null }));
      try {
        const result =
          await OtpBalanceService.updateNotificationContacts(contacts);
        setState((s) =>
          s.data
            ? {
                ...s,
                updatingContacts: false,
                data: {
                  ...s.data,
                  notification_contacts: result.notification_contacts,
                },
              }
            : { ...s, updatingContacts: false }
        );
      } catch (err) {
        setState((s) => ({
          ...s,
          updatingContacts: false,
          error:
            err instanceof Error
              ? err.message
              : "Error al actualizar los contactos",
        }));
        throw err;
      }
    },
    []
  );

  return { ...state, buyPack, updateNotificationContacts, reload };
}
